import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Star, Truck, ShieldCheck, RotateCcw, Sparkles, Minus, Plus } from "lucide-react";
import { api, formatPrice, img } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ProductDetails() {
  const { slug } = useParams();
  const { addToCart, toggleWishlist, inWishlist, addRecent, recent } = useStore();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [reco, setReco] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [zoom, setZoom] = useState({ x: 50, y: 50, on: false });
  const [form, setForm] = useState({ rating: 5, title: "", body: "", author: "" });
  const imgRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0); setActive(0); setQty(1); setNotFound(false);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data); addRecent(r.data);
      api.get(`/products/${slug}/related`).then((x) => setRelated(x.data));
      api.get(`/reviews/${r.data.id}`).then((x) => setReviews(x.data));
      api.post(`/recommendations`, { product_ids: [r.data.id] }).then((x) => setReco(x.data.items)).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="product-not-found">
      <h1 className="font-display text-4xl">Product Not Found</h1>
      <p className="text-muted-foreground mt-3">We couldn't find the item you're looking for.</p>
      <Link to="/shop"><Button className="mt-8 rounded-none bg-[#D4AF37] text-black">Back to Shop</Button></Link>
    </div>
  );

  if (!product) return <div className="mx-auto max-w-7xl px-6 py-20 grid lg:grid-cols-2 gap-12"><div className="aspect-square bg-[#F5F1E8] dark:bg-[#141414] animate-pulse" /><div className="space-y-4"><div className="h-8 w-2/3 bg-[#F5F1E8] dark:bg-[#141414] animate-pulse" /><div className="h-6 w-24 bg-[#F5F1E8] dark:bg-[#141414] animate-pulse" /></div></div>;

  const saved = inWishlist(product.id);
  const images = product.images?.length ? product.images : [product.images?.[0]];

  const submitReview = async (e) => {
    e.preventDefault();
    const r = await api.post("/reviews", { ...form, product_id: product.id });
    setReviews([r.data, ...reviews]); setForm({ rating: 5, title: "", body: "", author: "" });
    toast.success("Thank you for your review!");
  };

  const onMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    setZoom({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100, on: true });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-8"><Link to="/" className="hover:text-[#D4AF37]">Home</Link> / <Link to="/shop" className="hover:text-[#D4AF37]">Shop</Link> / <span className="text-foreground">{product.name}</span></nav>
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="flex gap-4">
          <div className="hidden sm:flex flex-col gap-3">
            {images.map((img_, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                className={`h-20 w-16 overflow-hidden border ${active === i ? "border-[#D4AF37]" : "border-transparent"}`}>
                <img src={img(img_)} alt="" className="h-full w-full object-cover" /></button>
            ))}
          </div>
          <div ref={imgRef} onMouseMove={onMove} onMouseLeave={() => setZoom((z) => ({ ...z, on: false }))}
            className="relative flex-1 aspect-[3/4] overflow-hidden bg-[#F5F1E8] dark:bg-[#141414] cursor-zoom-in" data-testid="product-main-image">
            <img src={img(images[active])} alt={product.name} className="h-full w-full object-cover transition-transform duration-200"
              style={zoom.on ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}} />
          </div>
        </div>

        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">{product.category}</p>
          <h1 className="font-display text-4xl mt-2" data-testid="product-name">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-muted-foreground"}`} />)}</div>
            <span className="text-sm text-muted-foreground">{product.rating} · {product.review_count} reviews</span>
          </div>
          <div className="flex items-center gap-3 mt-5">
            <span className="font-display text-3xl" data-testid="product-price">{formatPrice(product.price)}</span>
            {product.compare_at_price && <span className="text-xl text-muted-foreground line-through">{formatPrice(product.compare_at_price)}</span>}
          </div>
          <p className="text-muted-foreground mt-5 leading-relaxed">{product.description}</p>
          <p className={`mt-4 text-sm ${product.stock > 0 ? "text-green-600 dark:text-green-500" : "text-destructive"}`}>{product.stock > 0 ? `In stock · ${product.stock} available` : "Out of stock"}</p>

          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center border border-border">
              <button className="px-3 py-3" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-dec"><Minus className="h-4 w-4" /></button>
              <span className="px-4" data-testid="qty-value">{qty}</span>
              <button className="px-3 py-3" onClick={() => setQty(qty + 1)} data-testid="qty-inc"><Plus className="h-4 w-4" /></button>
            </div>
            <Button data-testid="add-to-cart-btn" onClick={() => addToCart(product, qty)} className="flex-1 rounded-none h-12 bg-[#D4AF37] text-black font-semibold hover:brightness-110">Add to Cart</Button>
            <Button variant="outline" onClick={() => toggleWishlist(product)} data-testid="wishlist-btn" className="rounded-none h-12 w-12 p-0 border-border"><Heart className={`h-5 w-5 ${saved ? "fill-[#D4AF37] text-[#D4AF37]" : ""}`} /></Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8 text-xs">
            {[[Truck, "Free shipping $75+"], [ShieldCheck, "Secure payment"], [RotateCcw, "30-day returns"]].map(([Icon, t], i) => (
              <div key={i} className="flex flex-col items-center gap-2 border border-border p-4 text-center"><Icon className="h-5 w-5 text-[#D4AF37]" />{t}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs: description & reviews */}
      <Tabs defaultValue="reviews" className="mt-20">
        <TabsList className="rounded-none bg-transparent border-b border-border w-full justify-start gap-6 h-auto p-0">
          <TabsTrigger value="reviews" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#D4AF37] px-0 pb-3">Reviews ({reviews.length})</TabsTrigger>
          <TabsTrigger value="shipping" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#D4AF37] px-0 pb-3">Shipping & Returns</TabsTrigger>
        </TabsList>
        <TabsContent value="reviews" className="pt-8 grid lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            {reviews.length === 0 && <p className="text-muted-foreground">No reviews yet. Be the first!</p>}
            {reviews.map((r) => (
              <div key={r.id} className="border-b border-border pb-5" data-testid="review-item">
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-muted-foreground"}`} />)}</div>
                <p className="font-display text-lg mt-2">{r.title}</p>
                <p className="text-muted-foreground text-sm mt-1">{r.body}</p>
                <p className="text-xs uppercase tracking-wide mt-2">— {r.author}</p>
              </div>
            ))}
          </div>
          <form onSubmit={submitReview} className="space-y-4 bg-[#F5F1E8] dark:bg-[#141414] p-8 h-fit">
            <h3 className="font-display text-2xl">Write a Review</h3>
            <div className="flex gap-1">{[1,2,3,4,5].map((n) => <button key={n} type="button" onClick={() => setForm({...form, rating: n})} data-testid={`rate-${n}`}><Star className={`h-6 w-6 ${n <= form.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-muted-foreground"}`} /></button>)}</div>
            <Input required value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} placeholder="Your name" className="rounded-none bg-background" data-testid="review-author" />
            <Input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Review title" className="rounded-none bg-background" data-testid="review-title" />
            <Textarea required value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} placeholder="Share your thoughts" className="rounded-none bg-background" data-testid="review-body" />
            <Button data-testid="submit-review" className="rounded-none bg-[#D4AF37] text-black font-semibold w-full hover:brightness-110">Submit Review</Button>
          </form>
        </TabsContent>
        <TabsContent value="shipping" className="pt-8 text-muted-foreground max-w-2xl leading-relaxed">
          <p>We ship worldwide via USPS, UPS, FedEx and DHL. Orders over $75 ship free. Most orders dispatch within 1–2 business days. Enjoy 30-day hassle-free returns on all items.</p>
        </TabsContent>
      </Tabs>

      {reco.length > 0 && (
        <section className="mt-20">
          <div className="flex items-center gap-2 mb-8"><Sparkles className="h-5 w-5 text-[#D4AF37]" /><h2 className="font-display text-3xl">You May Also Love</h2></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">{reco.slice(0,4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </section>
      )}
      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display text-3xl mb-8">Related Products</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">{related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </section>
      )}
      {recent.length > 1 && (
        <section className="mt-20">
          <h2 className="font-display text-3xl mb-8">Recently Viewed</h2>
          <div className="flex gap-5 overflow-x-auto hide-scrollbar pb-2">
            {recent.filter((r) => r.slug !== slug).map((r) => (
              <Link key={r.id} to={`/product/${r.slug}`} className="min-w-[160px] group">
                <div className="aspect-[3/4] overflow-hidden bg-[#F5F1E8] dark:bg-[#141414]"><img src={img(r.image)} alt={r.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <p className="font-display text-sm mt-2">{r.name}</p><p className="text-xs text-muted-foreground">{formatPrice(r.price)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
